import React, { useRef, useState, useEffect } from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import { IconSpinner } from '../../../components';
import { Button } from '@mui/material';
import css from './ApplicationForm.module.css';

const ProfilePhotoUpload = ({ name, value, onChange, uploadInProgress, uploadError }) => {
  const fileInputRef = useRef();
  const [isHovered, setIsHovered] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Create and cleanup object URL
  useEffect(() => {
    if (value && value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      
      // Cleanup function to revoke the object URL
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (value && value.url) {
      // Handle existing photo URL from database
      // Use presigned URL if available, otherwise fall back to original URL
      const displayUrl = value.url_presigned || value.url;
      setPreviewUrl(displayUrl);
    } else {
      setPreviewUrl(null);
    }
  }, [value]);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file && onChange) {
      onChange(file);
    }
  };

  const handleRemovePhoto = () => {
    if (onChange) {
      // If it's an existing photo with URL, we need to mark it for removal
      // The parent component should handle this by setting the value to null
      onChange(null);
    }
  };

  return (
    <div className={css.profilePhotoUploadSection}>
      <div className={css.profilePhotoRow}>
        <div
          className={css.profilePhotoPreviewCircle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ position: 'relative' }}
        >
          {previewUrl ? (
            <>
              <img src={previewUrl} alt="Profile preview" className={css.profilePhotoPreviewImg} />
              {isHovered && (
                <button
                  type="button"
                  className={css.profilePhotoRemoveButton}
                  onClick={handleRemovePhoto}
                  tabIndex={0}
                  aria-label="Remove photo"
                  disabled={uploadInProgress}
                >
                  ×
                </button>
              )}
            </>
          ) : (
            <span className={css.profilePhotoPlaceholder}>?</span>
          )}
        </div>
        <div className={css.profilePhotoUploadButtonWrapper}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            name={name}
            onChange={handleFileChange}
            disabled={uploadInProgress}
          />
          <Button
            type="button"
            variant="contained"
            onClick={handleButtonClick}
            disabled={uploadInProgress}
            className={css.addPetBtn}
            startIcon={uploadInProgress ? <IconSpinner /> : ''}
          >
            {uploadInProgress ? (
              <FormattedMessage id="ApplicationForm.uploading" defaultMessage="Uploading..." />
            ) : (
              <FormattedMessage id="ApplicationForm.uploadProfilePhotoButton" defaultMessage="Upload Photo" />
            )}
          </Button>
        </div>
      </div>
      {uploadError && (
        <div className={css.uploadError}>
          <FormattedMessage id="ApplicationForm.uploadError" />
        </div>
      )}
    </div>
  );
};

export default ProfilePhotoUpload; 